// Theme configuration for AvioTrack
// Defines 5 themes: Standard, Mono, Dark, Light, Custom
//
// This module owns the CSS variables only. The MAP side of a theme — basemap
// style, brightness and marker colours — lives in THEME_MAP_APPEARANCE in
// utils/mapStyles.js, which is the single source of truth for it. These used to
// be duplicated here as mapStyle/mapBrightness; the two copies drifted apart and
// whichever was written last silently won. Do not reintroduce them here.

// Theme definitions with all CSS variables
export const THEMES = {
  standard: {
    id: 'standard',
    name: 'Standard',
    description: 'Default dark theme with warm accents',
    variables: {
      // Core backgrounds
      '--bg-primary': '#3d4751',
      '--bg-secondary': '#4a5562',
      '--bg-tertiary': '#2a3139',
      '--bg-card': '#d4cfc1',
      '--bg-card-hover': '#c4bfb1',
      '--bg-card-active': '#b4afa1',

      // Text colors
      '--text-primary': '#ffffff',
      '--text-secondary': 'rgba(255, 255, 255, 0.8)',
      '--text-muted': 'rgba(255, 255, 255, 0.6)',
      '--text-on-card': '#333333',
      '--text-on-card-secondary': '#555555',
      '--text-on-card-muted': '#666666',

      // Accent colors
      '--accent-primary': '#FF6B35',
      '--accent-primary-hover': '#e55a2a',
      '--accent-primary-glow': 'rgba(255, 107, 53, 0.4)',
      '--accent-secondary': '#D4A520',

      // Status colors
      '--status-new': '#32CD32',
      '--status-new-bg': '#d4f4dd',
      '--status-new-bg-hover': '#c4e4cd',
      '--status-new-glow': 'rgba(50, 205, 50, 0.4)',
      '--status-military': '#C0C0C0',
      '--status-military-bg': '#d8d8d8',
      '--status-military-bg-hover': '#c8c8c8',
      '--status-military-text': '#333333',
      '--status-selected': '#FF6B35',
      '--status-selected-bg': '#FFD580',
      '--status-selected-bg-hover': '#FFC060',
      '--status-no-position-bg': '#b8c4d0',
      '--status-no-position-bg-hover': '#a8b4c0',
      '--status-not-regular': '#FF6B35',
      '--status-connected': '#28a745',
      '--status-connected-bg': '#d4edda',
      '--status-connected-text': '#155724',

      // Borders
      '--border-light': 'rgba(255, 255, 255, 0.1)',
      '--border-medium': 'rgba(255, 255, 255, 0.2)',
      '--border-on-card': '#bfbaac',
      '--border-on-card-light': '#ccc',

      // Stats overlay
      '--stats-bg': 'rgba(45, 55, 72, 0.75)',
      '--stats-bg-hover': 'rgba(55, 65, 82, 0.85)',
      '--stats-text': '#ffffff',
      '--stats-label': 'rgba(255, 255, 255, 0.7)',

      // Shadows
      '--shadow-sm': '0 1px 3px rgba(0, 0, 0, 0.2)',
      '--shadow-md': '0 2px 8px rgba(0, 0, 0, 0.25)',
      '--shadow-lg': '0 4px 20px rgba(0, 0, 0, 0.3)',

      // Table header
      '--table-header-bg': '#3d4751',
      '--table-header-text': '#ffffff',
      '--table-header-border': '#FF6B35',

      // Table body (light card-style tables)
      '--table-body-bg': '#d4cfc1',
      '--table-body-text': '#333333',
      '--table-body-text-secondary': '#555555',
      '--table-row-hover': '#c4bfb1',
      '--table-row-border': '#bfbaac',
      '--table-row-alt': '#ccc7b9',

      // Summary tables (dark background tables in stats sections)
      '--summary-table-bg': '#2d3748',
      '--summary-table-header-bg': '#1a202c',
      '--summary-table-text': '#e2e8f0',
      '--summary-table-text-secondary': '#a0aec0',
      '--summary-table-border': '#374151',
      '--summary-table-hover': '#374151',

      // Aircraft sprite icon filter (for use in tables)
      '--aircraft-icon-filter': 'brightness(0) saturate(100%) brightness(0.3)',
      '--aircraft-icon-filter-light': 'brightness(0) invert(1) opacity(0.9)',
      // Ground-vehicle (truck) icon: black — orange was unreadable
      '--ground-vehicle-color': '#000000',

      // Mobile controls
      '--mobile-controls-bg': 'linear-gradient(180deg, #4a5562 0%, #3d4751 100%)',
      '--mobile-tab-active': '#FF6B35',

      // Altitude chart
      '--altitude-chart-bg': 'linear-gradient(to bottom, #1a1a2e, #16213e)',

      // Nav icons (white icons on dark bg)
      '--nav-icon-filter': 'brightness(0) invert(1) opacity(0.8)',
      '--nav-icon-filter-active': 'brightness(0) invert(1) opacity(1)',
    }
  },

  mono: {
    id: 'mono',
    name: 'Mono',
    description: 'Monochrome theme with grayscale elements',
    variables: {
      // Core backgrounds - cooler grays
      '--bg-primary': '#1a1a1a',
      '--bg-secondary': '#252525',
      '--bg-tertiary': '#0f0f0f',
      '--bg-card': '#2a2a2a',
      '--bg-card-hover': '#353535',
      '--bg-card-active': '#404040',

      // Text colors - pure white/gray hierarchy
      '--text-primary': '#ffffff',
      '--text-secondary': '#b0b0b0',
      '--text-muted': '#707070',
      '--text-on-card': '#e0e0e0',
      '--text-on-card-secondary': '#a0a0a0',
      '--text-on-card-muted': '#707070',

      // Accent colors - grayscale with slight warmth
      '--accent-primary': '#808080',
      '--accent-primary-hover': '#909090',
      '--accent-primary-glow': 'rgba(128, 128, 128, 0.3)',
      '--accent-secondary': '#606060',

      // Status colors - all grayscale for mono theme
      '--status-new': '#c0c0c0',
      '--status-new-bg': '#2a2a2a',
      '--status-new-bg-hover': '#353535',
      '--status-new-glow': 'rgba(192, 192, 192, 0.3)',
      '--status-military': '#808080',
      '--status-military-bg': '#2a2a2a',
      '--status-military-bg-hover': '#353535',
      '--status-military-text': '#ffffff',
      '--status-selected': '#ffffff',
      '--status-selected-bg': '#404040',
      '--status-selected-bg-hover': '#505050',
      '--status-no-position-bg': '#1f1f1f',
      '--status-no-position-bg-hover': '#2a2a2a',
      '--status-not-regular': '#909090',
      '--status-connected': '#808080',
      '--status-connected-bg': '#2a2a2a',
      '--status-connected-text': '#c0c0c0',

      // Borders
      '--border-light': 'rgba(255, 255, 255, 0.05)',
      '--border-medium': 'rgba(255, 255, 255, 0.1)',
      '--border-on-card': '#404040',
      '--border-on-card-light': '#353535',

      // Stats overlay
      '--stats-bg': 'rgba(26, 26, 26, 0.85)',
      '--stats-bg-hover': 'rgba(40, 40, 40, 0.9)',
      '--stats-text': '#ffffff',
      '--stats-label': 'rgba(255, 255, 255, 0.5)',

      // Shadows - softer
      '--shadow-sm': '0 1px 3px rgba(0, 0, 0, 0.4)',
      '--shadow-md': '0 2px 8px rgba(0, 0, 0, 0.5)',
      '--shadow-lg': '0 4px 20px rgba(0, 0, 0, 0.6)',

      // Table header
      '--table-header-bg': '#1a1a1a',
      '--table-header-text': '#ffffff',
      '--table-header-border': '#505050',

      // Table body (dark themed tables)
      '--table-body-bg': '#2a2a2a',
      '--table-body-text': '#e0e0e0',
      '--table-body-text-secondary': '#a0a0a0',
      '--table-row-hover': '#353535',
      '--table-row-border': '#404040',
      '--table-row-alt': '#252525',

      // Summary tables (same as main tables in mono)
      '--summary-table-bg': '#1f1f1f',
      '--summary-table-header-bg': '#1a1a1a',
      '--summary-table-text': '#e0e0e0',
      '--summary-table-text-secondary': '#808080',
      '--summary-table-border': '#333333',
      '--summary-table-hover': '#2a2a2a',

      // Aircraft sprite icon filter (inverted for dark tables)
      '--aircraft-icon-filter': 'brightness(0) invert(1) opacity(0.7)',
      '--aircraft-icon-filter-light': 'brightness(0) invert(1) opacity(0.9)',
      // Ground-vehicle (truck) icon: light on dark tables
      '--ground-vehicle-color': '#e6e6e6',

      // Mobile controls
      '--mobile-controls-bg': 'linear-gradient(180deg, #252525 0%, #1a1a1a 100%)',
      '--mobile-tab-active': '#606060',

      // Nav icons (white icons on dark bg)
      '--nav-icon-filter': 'brightness(0) invert(1) opacity(0.8)',
      '--nav-icon-filter-active': 'brightness(0) invert(1) opacity(1)',

      // Altitude chart
      '--altitude-chart-bg': 'linear-gradient(to bottom, #1a1a1a, #0f0f0f)',

      // Mono theme filters - greyscale everything
      '--logo-filter': 'grayscale(1) brightness(1.2)',
      '--airline-logo-filter': 'grayscale(1)',
      '--flag-filter': 'grayscale(1)',
      '--aircraft-sprite-filter': 'brightness(0) invert(1) opacity(0.7)',
      '--global-image-filter': 'grayscale(1)',
    }
  },

  dark: {
    id: 'dark',
    name: 'Dark',
    description: 'Dark map with vibrant colored elements',
    variables: {
      // Core backgrounds - deep dark with blue tint
      '--bg-primary': '#0d1117',
      '--bg-secondary': '#161b22',
      '--bg-tertiary': '#010409',
      '--bg-card': '#21262d',
      '--bg-card-hover': '#30363d',
      '--bg-card-active': '#3d444d',

      // Text colors - bright for contrast
      '--text-primary': '#f0f6fc',
      '--text-secondary': '#c9d1d9',
      '--text-muted': '#8b949e',
      '--text-on-card': '#f0f6fc',
      '--text-on-card-secondary': '#c9d1d9',
      '--text-on-card-muted': '#8b949e',

      // Accent colors - vibrant orange/coral
      '--accent-primary': '#f97316',
      '--accent-primary-hover': '#ea580c',
      '--accent-primary-glow': 'rgba(249, 115, 22, 0.4)',
      '--accent-secondary': '#fb923c',

      // Status colors - vivid
      '--status-new': '#22c55e',
      '--status-new-bg': 'rgba(34, 197, 94, 0.15)',
      '--status-new-bg-hover': 'rgba(34, 197, 94, 0.25)',
      '--status-military': '#94a3b8',
      '--status-military-bg': 'rgba(148, 163, 184, 0.15)',
      '--status-military-bg-hover': 'rgba(148, 163, 184, 0.25)',
      '--status-military-text': '#e2e8f0',
      '--status-selected': '#f97316',
      '--status-selected-bg': 'rgba(249, 115, 22, 0.2)',
      '--status-selected-bg-hover': 'rgba(249, 115, 22, 0.3)',
      '--status-no-position-bg': 'rgba(71, 85, 105, 0.3)',
      '--status-no-position-bg-hover': 'rgba(71, 85, 105, 0.4)',

      // Borders - subtle glow
      '--border-light': 'rgba(240, 246, 252, 0.1)',
      '--border-medium': 'rgba(240, 246, 252, 0.15)',
      '--border-on-card': '#30363d',
      '--border-on-card-light': '#21262d',

      // Stats overlay
      '--stats-bg': 'rgba(13, 17, 23, 0.8)',
      '--stats-bg-hover': 'rgba(22, 27, 34, 0.9)',
      '--stats-text': '#f0f6fc',
      '--stats-label': 'rgba(240, 246, 252, 0.7)',

      // Shadows - with color tint
      '--shadow-sm': '0 1px 3px rgba(0, 0, 0, 0.4)',
      '--shadow-md': '0 2px 8px rgba(0, 0, 0, 0.5)',
      '--shadow-lg': '0 4px 20px rgba(0, 0, 0, 0.6)',

      // Table header
      '--table-header-bg': '#0d1117',
      '--table-header-text': '#f0f6fc',
      '--table-header-border': '#f97316',

      // Table body (dark themed tables)
      '--table-body-bg': '#21262d',
      '--table-body-text': '#f0f6fc',
      '--table-body-text-secondary': '#c9d1d9',
      '--table-row-hover': '#30363d',
      '--table-row-border': '#30363d',
      '--table-row-alt': '#161b22',

      // Summary tables
      '--summary-table-bg': '#161b22',
      '--summary-table-header-bg': '#0d1117',
      '--summary-table-text': '#f0f6fc',
      '--summary-table-text-secondary': '#8b949e',
      '--summary-table-border': '#21262d',
      '--summary-table-hover': '#21262d',

      // Aircraft sprite icon filter (inverted for dark tables)
      '--aircraft-icon-filter': 'brightness(0) invert(1) opacity(0.8)',
      '--aircraft-icon-filter-light': 'brightness(0) invert(1) opacity(0.9)',
      // Ground-vehicle (truck) icon: light on dark tables
      '--ground-vehicle-color': '#e6e6e6',

      // Mobile controls
      // Standard gray for the mobile header/nav bars - the near-black
      // gradient made the phone chrome read as pure black.
      '--mobile-controls-bg': 'linear-gradient(180deg, #4a5562 0%, #3d4751 100%)',
      '--mobile-tab-active': '#f97316',

      // Altitude chart
      '--altitude-chart-bg': 'linear-gradient(to bottom, #0d1117, #010409)',

      // Nav icons (white icons on dark bg)
      '--nav-icon-filter': 'brightness(0) invert(1) opacity(0.8)',
      '--nav-icon-filter-active': 'brightness(0) invert(1) opacity(1)',
    }
  },

  light: {
    id: 'light',
    name: 'Light',
    description: 'Clean light theme for bright environments',
    variables: {
      // Core backgrounds - warm white
      '--bg-primary': '#f8f9fa',
      '--bg-secondary': '#ffffff',
      '--bg-tertiary': '#e9ecef',
      '--bg-card': '#ffffff',
      '--bg-card-hover': '#f1f3f4',
      '--bg-card-active': '#e8eaed',

      // Text colors - dark for readability
      '--text-primary': '#1a1a2e',
      '--text-secondary': '#4a4a5a',
      '--text-muted': '#6c757d',
      '--text-on-card': '#1a1a2e',
      '--text-on-card-secondary': '#4a4a5a',
      '--text-on-card-muted': '#6c757d',

      // Accent colors - warm orange/coral
      '--accent-primary': '#e85d04',
      '--accent-primary-hover': '#dc5800',
      '--accent-primary-glow': 'rgba(232, 93, 4, 0.2)',
      '--accent-secondary': '#f48c06',

      // Status colors
      '--status-new': '#16a34a',
      '--status-new-bg': '#dcfce7',
      '--status-new-bg-hover': '#bbf7d0',
      '--status-new-glow': 'rgba(22, 163, 74, 0.3)',
      '--status-military': '#64748b',
      '--status-military-bg': '#e2e8f0',
      '--status-military-bg-hover': '#cbd5e1',
      '--status-military-text': '#1e293b',
      '--status-selected': '#e85d04',
      '--status-selected-bg': '#fed7aa',
      '--status-selected-bg-hover': '#fdba74',
      '--status-no-position-bg': '#f1f5f9',
      '--status-no-position-bg-hover': '#e2e8f0',
      '--status-not-regular': '#e85d04',
      '--status-connected': '#16a34a',
      '--status-connected-bg': '#dcfce7',
      '--status-connected-text': '#166534',
      '--status-warning': '#f59e0b',
      '--status-warning-bg': '#fef3c7',
      '--status-warning-text': '#92400e',
      '--status-disconnected': '#6b7280',
      '--status-disconnected-bg': '#f3f4f6',
      '--status-disconnected-text': '#374151',
      '--status-error': '#dc2626',
      '--status-error-bg': '#fee2e2',
      '--status-error-text': '#991b1b',

      // Borders
      '--border-light': 'rgba(0, 0, 0, 0.06)',
      '--border-medium': 'rgba(0, 0, 0, 0.1)',
      '--border-on-card': '#e5e7eb',
      '--border-on-card-light': '#f3f4f6',

      // Stats overlay
      '--stats-bg': 'rgba(255, 255, 255, 0.9)',
      '--stats-bg-hover': 'rgba(255, 255, 255, 0.95)',
      '--stats-text': '#1a1a2e',
      '--stats-label': 'rgba(26, 26, 46, 0.6)',

      // Shadows - soft
      '--shadow-sm': '0 1px 3px rgba(0, 0, 0, 0.08)',
      '--shadow-md': '0 2px 8px rgba(0, 0, 0, 0.1)',
      '--shadow-lg': '0 4px 20px rgba(0, 0, 0, 0.12)',

      // Table header
      '--table-header-bg': '#1a1a2e',
      '--table-header-text': '#ffffff',
      '--table-header-border': '#e85d04',

      // Table body (light themed tables)
      '--table-body-bg': '#ffffff',
      '--table-body-text': '#1a1a2e',
      '--table-body-text-secondary': '#4a4a5a',
      '--table-row-hover': '#f1f3f4',
      '--table-row-border': '#e5e7eb',
      '--table-row-alt': '#f8f9fa',

      // Summary tables (darker section tables on light theme)
      '--summary-table-bg': '#1a1a2e',
      '--summary-table-header-bg': '#0f0f1a',
      '--summary-table-text': '#ffffff',
      '--summary-table-text-secondary': '#a0aec0',
      '--summary-table-border': '#2d2d4a',
      '--summary-table-hover': '#2a2a40',

      // Aircraft sprite icon filter (dark for light table, light for summary tables)
      '--aircraft-icon-filter': 'brightness(0) saturate(100%) brightness(0.3)',
      '--aircraft-icon-filter-light': 'brightness(0) invert(1) opacity(0.9)',
      // Ground-vehicle (truck) icon: black — orange was unreadable
      '--ground-vehicle-color': '#000000',

      // Mobile controls
      '--mobile-controls-bg': 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)',
      '--mobile-tab-active': '#e85d04',
      '--mobile-select-bg': 'rgba(0, 0, 0, 0.08)',
      '--mobile-tabs-bg': 'rgba(0, 0, 0, 0.1)',
      '--mobile-select-arrow': 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%231a1a2e\' d=\'M6 8L2 4h8z\'/%3E%3C/svg%3E")',

      // Nav icons (dark icons on light bg)
      '--nav-icon-filter': 'brightness(0) opacity(0.6)',
      '--nav-icon-filter-active': 'brightness(0) opacity(0.9)',

      // Altitude chart - light theme uses light gradient
      '--altitude-chart-bg': 'linear-gradient(to bottom, #e9ecef, #f8f9fa)',

      // Logo filter - make text black on light background
      '--logo-filter': 'brightness(0)',

      // Color scheme for native elements
      '--color-scheme': 'light',
    }
  },

  custom: {
    id: 'custom',
    name: 'Custom',
    description: 'Personalize your own color scheme',
    isCustomizable: true,
    // Custom theme starts as a copy of standard
    variables: null // Will be populated from localStorage or standard
  }
};

// Default theme
export const DEFAULT_THEME = 'standard';

// Storage key
const STORAGE_KEY_THEME = 'appTheme';
const STORAGE_KEY_CUSTOM_THEME = 'customThemeVariables';

// Get current theme settings
export function getThemeSettings() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_THEME);
    if (stored) {
      const settings = JSON.parse(stored);
      if (THEMES[settings.themeId]) {
        return {
          themeId: settings.themeId,
          mapStyleOverride: settings.mapStyleOverride || null
        };
      }
    }
  } catch (e) {
    console.error('Error reading theme settings:', e);
  }
  return {
    themeId: DEFAULT_THEME,
    mapStyleOverride: null
  };
}

// Save theme settings
export function saveThemeSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY_THEME, JSON.stringify(settings));
  } catch (e) {
    console.error('Error saving theme settings:', e);
  }
}

// Get custom theme variables
export function getCustomThemeVariables() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_CUSTOM_THEME);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading custom theme:', e);
  }
  // Return standard theme variables as default for custom
  return { ...THEMES.standard.variables };
}

// Save custom theme variables
export function saveCustomThemeVariables(variables) {
  try {
    localStorage.setItem(STORAGE_KEY_CUSTOM_THEME, JSON.stringify(variables));
  } catch (e) {
    console.error('Error saving custom theme:', e);
  }
}

// Get the full theme object with resolved variables
export function getResolvedTheme(themeId) {
  const theme = THEMES[themeId];
  if (!theme) {
    return THEMES[DEFAULT_THEME];
  }

  if (themeId === 'custom') {
    return {
      ...theme,
      variables: getCustomThemeVariables()
    };
  }

  return theme;
}

/**
 * Every CSS variable that ANY theme can set, including the user's saved custom
 * theme. applyTheme clears all of these before applying the incoming theme.
 *
 * Not cached: the custom theme can change at runtime, and this runs only on a
 * theme switch (a handful of times per session) over ~5 small objects.
 */
function allThemeVariableKeys() {
  const keys = new Set();
  Object.values(THEMES).forEach(theme => {
    Object.keys(theme.variables || {}).forEach(key => keys.add(key));
  });
  try {
    Object.keys(getCustomThemeVariables() || {}).forEach(key => keys.add(key));
  } catch (_) {
    // Custom theme unreadable — the built-in keys are still cleared.
  }
  return keys;
}

// Apply theme to document
export function applyTheme(themeId) {
  const theme = getResolvedTheme(themeId);
  const root = document.documentElement;

  // Clear every variable any theme could have set BEFORE applying the new one.
  //
  // These are written as inline styles on <html>, which outrank every rule in
  // theme.css. Without this clear, a variable defined by theme A but not by
  // theme B survived the switch to B — and switching back to A could never
  // restore the original, because A's own values were only re-applied for the
  // keys A defines. That is the "change theme, change back, and the colours /
  // logo / brightness are wrong" bug: the root element accumulated a union of
  // every theme ever selected.
  allThemeVariableKeys().forEach(key => root.style.removeProperty(key));

  // Apply all CSS variables
  if (theme.variables) {
    Object.entries(theme.variables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }

  // Set theme attribute for CSS selectors
  root.setAttribute('data-theme', themeId);

  return theme;
}

// Get all customizable variable groups for the settings UI
export function getCustomizableGroups() {
  return [
    {
      id: 'backgrounds',
      name: 'Backgrounds',
      variables: [
        { key: '--bg-primary', name: 'Header', type: 'color' },
        { key: '--bg-secondary', name: 'Page Background', type: 'color' },
        { key: '--bg-tertiary', name: 'Map Container', type: 'color' },
        { key: '--bg-card', name: 'Card/Table', type: 'color' },
      ]
    },
    {
      id: 'text',
      name: 'Text Colors',
      variables: [
        { key: '--text-primary', name: 'Primary Text', type: 'color' },
        { key: '--text-secondary', name: 'Secondary Text', type: 'color' },
        { key: '--text-on-card', name: 'Card Text', type: 'color' },
      ]
    },
    {
      id: 'accents',
      name: 'Accent Colors',
      variables: [
        { key: '--accent-primary', name: 'Primary Accent', type: 'color' },
        { key: '--table-header-border', name: 'Table Header Border', type: 'color' },
        { key: '--mobile-tab-active', name: 'Active Tab', type: 'color' },
      ]
    },
    {
      id: 'status',
      name: 'Status Colors',
      variables: [
        { key: '--status-new', name: 'New Aircraft', type: 'color' },
        { key: '--status-military', name: 'Military', type: 'color' },
        { key: '--status-selected', name: 'Selected', type: 'color' },
      ]
    }
  ];
}

// Reset custom theme to standard
export function resetCustomTheme() {
  const standardVars = { ...THEMES.standard.variables };
  saveCustomThemeVariables(standardVars);
  return standardVars;
}
